import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useIsAdmin } from "@/lib/portal/useIsAdmin";
import { Users, Ticket, CreditCard, Activity, ArrowRight, Zap } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const Route = createFileRoute("/_authenticated/portal/admin/")({
  component: AdminOverview,
});

function AdminOverview() {
  const { data: isAdmin, isLoading } = useIsAdmin();

  const { data } = useQuery({
    queryKey: ["admin", "overview"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const [projects, tickets, subs, invoices, recentTickets] = await Promise.all([
        supabase.from("projects").select("id, name, owner_id, plan, status, progress, current_step, delivery_date"),
        supabase.from("tickets").select("id, status, priority, created_at"),
        supabase.from("subscriptions").select("id, price, currency, status, plan"),
        supabase.from("invoices").select("id, amount, currency, status"),
        supabase.from("tickets").select("id, code, title, status, priority, created_at, project_id").order("created_at", { ascending: false }).limit(8),
      ]);
      return {
        projects: projects.data ?? [],
        tickets: tickets.data ?? [],
        subs: subs.data ?? [],
        invoices: invoices.data ?? [],
        recentTickets: recentTickets.data ?? [],
      };
    },
  });

  if (isLoading) return <div className="text-white/60 text-sm">Verifica accesso…</div>;
  if (!isAdmin) {
    throw redirect({ to: "/portal" });
  }

  const totalClients = new Set((data?.projects ?? []).map((p) => p.owner_id)).size;
  const openTickets = (data?.tickets ?? []).filter((t) => !["completed", "closed"].includes(t.status)).length;
  const urgentTickets = (data?.tickets ?? []).filter((t) => t.priority === "urgent" && t.status !== "completed").length;
  const mrr = (data?.subs ?? []).filter((s) => s.status === "active").reduce((sum, s) => sum + Number(s.price ?? 0), 0);
  const pendingInvoices = (data?.invoices ?? []).filter((i) => i.status === "pending" || i.status === "overdue").reduce((sum, i) => sum + Number(i.amount ?? 0), 0);
  const projectsInProgress = (data?.projects ?? []).filter((p) => p.status === "in_progress" || p.status === "active").length;

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-fuchsia-300 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full px-3 py-1">
          <Zap className="h-3 w-3" /> Admin Console
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Overview clienti</h1>
        <p className="text-white/60 mt-1">Tutti i progetti, ticket e ricavi FlashWeb in un unico posto.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI icon={Users} label="Clienti attivi" value={String(totalClients)} sub={`${projectsInProgress} progetti in lavorazione`} tone="fuchsia" />
        <KPI icon={Ticket} label="Ticket aperti" value={String(openTickets)} sub={urgentTickets > 0 ? `${urgentTickets} URGENTI` : "Tutto sotto controllo"} tone={urgentTickets > 0 ? "red" : "cyan"} />
        <KPI icon={CreditCard} label="MRR" value={`CHF ${mrr.toLocaleString("it-CH")}`} sub={`${(data?.subs ?? []).filter((s) => s.status === "active").length} abbonamenti attivi`} tone="emerald" />
        <KPI icon={Activity} label="Fatture pending" value={`CHF ${pendingInvoices.toLocaleString("it-CH")}`} sub={`${(data?.invoices ?? []).filter((i) => i.status === "pending" || i.status === "overdue").length} documenti`} tone="amber" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className={`${glass} p-6 lg:col-span-2`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Ticket recenti</h2>
            <Link to="/portal/admin/tickets" className="text-xs text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center gap-1">
              Vedi tutti <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-white/5">
            {(data?.recentTickets ?? []).map((t) => (
              <Link key={t.id} to="/portal/admin/tickets" className="flex items-center justify-between py-3 hover:bg-white/[0.02] px-2 -mx-2 rounded-lg">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[11px] text-white/40 font-mono">
                    <span>{t.code}</span>
                    <span>·</span>
                    <span>{format(new Date(t.created_at), "d MMM HH:mm", { locale: it })}</span>
                  </div>
                  <div className="mt-0.5 font-medium truncate">{t.title}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.priority === "urgent" ? "bg-red-500/20 text-red-300" : "bg-white/5 text-white/60"}`}>{t.priority}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 capitalize">{t.status.replace("_", " ")}</span>
                </div>
              </Link>
            ))}
            {(data?.recentTickets ?? []).length === 0 && <div className="py-8 text-center text-sm text-white/40">Nessun ticket ancora</div>}
          </div>
        </div>

        <div className={`${glass} p-6`}>
          <h2 className="font-semibold mb-4">Progetti attivi</h2>
          <div className="space-y-3">
            {(data?.projects ?? []).slice(0, 6).map((p) => (
              <Link key={p.id} to="/portal/admin/projects" className="block p-3 rounded-xl bg-white/[0.02] hover:bg-white/5 border border-white/5">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm truncate pr-2">{p.name}</div>
                  <span className="text-[10px] uppercase tracking-widest text-white/50">{p.plan}</span>
                </div>
                <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${p.progress ?? 0}%` }} />
                </div>
                <div className="mt-1.5 text-[11px] text-white/50">{p.current_step ?? "—"}</div>
              </Link>
            ))}
            {(data?.projects ?? []).length === 0 && <div className="py-6 text-center text-sm text-white/40">Nessun progetto</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, tone }: { icon: typeof Users; label: string; value: string; sub: string; tone: "fuchsia" | "cyan" | "emerald" | "amber" | "red" }) {
  const tones = {
    fuchsia: "from-fuchsia-500/20 to-fuchsia-500/5 text-fuchsia-300 ring-fuchsia-500/30",
    cyan: "from-cyan-500/20 to-cyan-500/5 text-cyan-300 ring-cyan-500/30",
    emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-300 ring-emerald-500/30",
    amber: "from-amber-500/20 to-amber-500/5 text-amber-300 ring-amber-500/30",
    red: "from-red-500/20 to-red-500/5 text-red-300 ring-red-500/30",
  };
  return (
    <div className={`${glass} p-5`}>
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${tones[tone]} ring-1`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-4 text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      <div className="mt-1 text-xs text-white/50">{sub}</div>
    </div>
  );
}
